import React, {useEffect, useState} from 'react';
import {View, TouchableOpacity, StyleSheet, Alert} from 'react-native';
import Icon from 'design-system/src/Components/Icon/Icon';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import {ISession} from '~Api/TraceApi/endpoints/sessions.endpoint';
import defaultTheme from 'design-system/src/Theme/defaultTheme';
import {CustomActivityIndicator} from 'design-system/src/Components/CustomActivityIndicator';
import Metrics from 'design-system/src/Theme/Metrics';
import Button from 'design-system/src/Components/Button/Button';
import Typography from 'design-system/src/Components/Typography/Typography';
import {formatDistance} from 'date-fns';
import {fr, enUS} from 'date-fns/locale';
import {useCurrentLanguage} from '~Store/Slices/LanguageSlice/Language.selector';
import {useSession} from '@src/Providers/SessionProvider';
import useAppNavigation from '@src/utils/useAppNavigation';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';

const locales: {[key: string]: Locale} = {
  fr,
  en: enUS,
};

const SessionManagement: React.FC = () => {
  const {t} = useTranslation('session');
  const navigation = useAppNavigation();
  const currentLang = useCurrentLanguage();
  const {
    sessionId,
    userSessions,
    revokeSession,
    revokeOtherSessions,
    refreshSessions,
    maxSessionsReached,
    handleExpiredSession,
    heartBeat,
  } = useSession();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [isRevokeOthersModalOpen, setIsRevokeOthersModalOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (maxSessionsReached) {
      Alert.alert(
        t('Limite de sessions atteinte'),
        t(
          'Vous avez atteint la limite de 3 sessions actives. Veuillez en révoquer une pour continuer',
        ) as string,
        [{text: t('OK') as string}],
      );
    }
  }, [maxSessionsReached]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshSessions();
    await heartBeat();
    setRefreshing(false);
  };

  useEffect(() => {
    handleRefresh();
  }, []);

  const handleRevoke = async (targetSessionId: string) => {
    setSelectedSessionId(targetSessionId);
    setIsRevokeModalOpen(true);
  };

  const handleConfirmRevoke = async () => {
    if (!selectedSessionId) return;
    setLoading(true);
    try {
      if (selectedSessionId === sessionId) {
        await handleExpiredSession();
      } else {
        await revokeSession(selectedSessionId);
      }
    } catch (error) {
      Alert.alert(
        t('Erreur'),
        t('Erreur lors de la révocation de la session') as string,
      );
      console.error(error);
    } finally {
      setLoading(false);
      setIsRevokeModalOpen(false);
      setSelectedSessionId(null);
    }
  };

  const handleRevokeOthers = () => {
    setIsRevokeOthersModalOpen(true);
  };

  const handleConfirmRevokeOthers = async () => {
    setLoading(true);
    try {
      await revokeOtherSessions();
    } catch (error) {
      Alert.alert(
        t('Erreur'),
        t('Erreur lors de la révocation des sessions') as string,
      );
      console.error(error);
    } finally {
      setLoading(false);
      setIsRevokeOthersModalOpen(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    return formatDistance(date, new Date(), {
      addSuffix: true,
      locale: locales[currentLang] || enUS,
    });
  };

  const getDeviceName = (session: ISession) => {
    const deviceInfo = session.deviceInfo || {};
    const platform = session.isTvSession
      ? deviceInfo.name || 'TV'
      : deviceInfo.platform || '';
    let browser = deviceInfo.browser || '';
    const userAgent = deviceInfo.userAgent || '';

    // Extract browser name from user agent
    if (userAgent) {
      if (userAgent.includes('Chrome')) browser = 'Chrome';
      else if (userAgent.includes('Firefox')) browser = 'Firefox';
      else if (userAgent.includes('Safari')) browser = 'Safari';
      else if (userAgent.includes('Edge')) browser = 'Edge';
      else if (userAgent.includes('Opera')) browser = 'Opera';
    }

    let name = session.isTvSession ? deviceInfo.name : '';

    if (deviceInfo.name) {
      if (deviceInfo.name.includes('iPhone')) name = 'iPhone';
      if (deviceInfo.name.includes('iPad')) name = 'iPad';
      if (deviceInfo.name.includes('Android')) name = 'Android Device';
      if (deviceInfo.name.includes('Windows')) name = 'Windows PC';
      if (deviceInfo.name.includes('Mac')) name = 'Mac';
      if (deviceInfo.name.includes('Linux')) name = 'Linux';
    }

    return `${name} - ${browser || platform}`.trim() || t('Appareil inconnu');
  };

  const renderSessionItem = ({item}: {item: ISession}) => {
    const isCurrentSession = item._id === sessionId;
    return (
      <View
        style={[styles.sessionItem, isCurrentSession && styles.currentSession]}>
        <View style={styles.sessionInfo}>
          <Typography variant="body2" style={styles.deviceName}>
            {getDeviceName(item) || t('Appareil inconnu')}
            {isCurrentSession && (
              <Typography variant="body2" style={styles.currentLabel}>
                {' '}
                ({t('Session actuelle')})
              </Typography>
            )}
          </Typography>
          <Typography variant="body3" style={styles.sessionDetail}>
            {t('Créée le') + ': ' + formatDate(item.createdAt)}
          </Typography>
          <Typography variant="body3" style={styles.sessionDetail}>
            {t('Dernière activité') +
              ': ' +
              formatDate(item.lastActivity) +
              ' (' +
              formatRelativeTime(item.lastActivity) +
              ')'}
          </Typography>
        </View>
        <TouchableOpacity
          style={styles.revokeButton}
          onPress={() => handleRevoke(item._id)}
          disabled={loading}>
          <Typography variant="button3" style={styles.revokeButtonText}>
            {t('Révoquer')}
          </Typography>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.safeArea}>
      <View style={styles.header}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            flex: 1,
            alignItems: 'center',
            // borderBottomWidth: 1,
            // borderBottomColor: '#eee',
            paddingBottom: Metrics.verticalScale(8),
          }}>
          <Typography variant="h2">{t('Gestion des Sessions')}</Typography>
          <TouchableOpacity
            onPress={() => handleRefresh()}
            style={styles.backButton}>
            <Icon name="restore" />
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.container}>
        <View style={styles.infoBox}>
          <Typography variant="body2" style={styles.infoText}>
            {t("Vous pouvez avoir jusqu'à 3 sessions actives simultanément")}.
          </Typography>
        </View>
        <Typography variant="h4" style={styles.subtitle}>
          {t('Vos sessions actives')} ({userSessions.length}/3)
        </Typography>
        {loading && (
          <CustomActivityIndicator
            style={[StyleSheet.absoluteFill, {zIndex: 1}]}
            size="large"
          />
        )}
        <View
          style={{
            flex: 1,
            marginBottom: 20,
          }}>
          <View>
            {userSessions.length === 0 ? (
              <Typography style={styles.emptyText}>
                {t('Aucune session active')}.
              </Typography>
            ) : (
              userSessions.map((item, index) => (
                <React.Fragment key={index}>
                  {renderSessionItem({item})}
                </React.Fragment>
              ))
            )}
          </View>
          {userSessions.length > 1 ? (
            <View
              style={{
                alignSelf: 'stretch',
                flexDirection: 'row',
                marginVertical: Metrics.verticalScale(16),
              }}>
              <Button
                variant="outlined"
                size="small"
                title={t('Révoquer toutes les autres sessions')}
                onPress={handleRevokeOthers}
                fullWidth
              />
            </View>
          ) : undefined}
        </View>
      </View>
      <ConfirmationModal
        isOpen={isRevokeModalOpen}
        onClose={() => setIsRevokeModalOpen(false)}
        onConfirm={handleConfirmRevoke}
        title={t('Révoquer la session')}
        message={t('Êtes-vous sûr de vouloir révoquer cette session ?')}
        confirmText={t('Révoquer')}
        cancelText={t('Annuler')}
        loading={loading}
      />
      <ConfirmationModal
        isOpen={isRevokeOthersModalOpen}
        onClose={() => setIsRevokeOthersModalOpen(false)}
        onConfirm={handleConfirmRevokeOthers}
        title={t('Révoquer les autres sessions')}
        message={t(
          'Êtes-vous sûr de vouloir révoquer toutes les autres sessions ?',
        )}
        confirmText={t('Révoquer')}
        cancelText={t('Annuler')}
        loading={loading}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    height: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingHorizontal: Metrics.horizontalScale(16),
    // height: headerHeight,
  },
  backButton: {
    padding: 8,
  },
  container: {
    flex: 1,
    padding: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  subtitle: {
    marginTop: 16,
    marginBottom: 8,
  },
  infoBox: {
    backgroundColor: 'rgba(234, 108, 19, 0.13)',
    borderLeftWidth: 4,
    borderLeftColor: '#ea6c13',
    padding: 12,
    marginBottom: 16,
  },
  infoText: {
    marginBottom: 4,
  },
  sessionsList: {
    flex: 1,
  },
  sessionItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    marginBottom: 2,
  },
  currentSession: {
    backgroundColor: 'rgba(5, 201, 107, 0.2)',
  },
  sessionInfo: {
    flex: 1,
  },
  deviceName: {
    marginBottom: 4,
  },
  currentLabel: {
    color: '#52c41a',
  },
  sessionDetail: {
    marginBottom: 2,
  },
  revokeButton: {
    backgroundColor: defaultTheme.primary,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 4,
  },
  revokeButtonText: {
    color: 'white',
  },
  revokeOthersButtonText: {
    color: 'white',
  },
  emptyText: {
    textAlign: 'center',
    marginTop: 20,
    color: '#666',
  },
});

export default SessionManagement;
